import React, { Fragment, useState, useCallback } from "react";
import { Col, Container, Row } from "reactstrap";
import jsPDF from "jspdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DownloadIcon from '../../../../../../src/designUtils/ProjectDashboard/DownloadIcon.svg'
import {
  faDownload
} from "@fortawesome/free-solid-svg-icons";
const CatalogueItemDetails = ({ data }) => {
  const [expandedSections, setExpandedSections] = useState({
    attributes: false,
    externalIdentifiers: false,
    dimensions: false,
    keyFeatures: false,
    description: false,
    identifiers: false,
    images: false,
    productTypes: false,
    relationships: false,
    salesRanks: false,
    summaries: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderValue = (val) => {
    if (val === null || val === undefined) return "-";
    if (typeof val === "string" || typeof val === "number" || typeof val === "boolean")
      return String(val);
    if (Array.isArray(val)) return val.map(renderValue).join(", ");
    if (typeof val === "object") {
      const parts = [];
      for (const key in val) {
        if (Object.prototype.hasOwnProperty.call(val, key)) {
          parts.push(key + ": " + renderValue(val[key]));
        }
      }
      return parts.join("; ");
    }
    return String(val);
  };

  const attributes = data && data.attributes || {};

  const getAttr = (key) => {
    const attr = attributes[key] && attributes[key][0];
    if (!attr) return "-";
    if (attr.value !== undefined && attr.value !== null) return renderValue(attr.value);
    if (typeof attr === "object") return renderValue(attr);
    return renderValue(attr);
  };

  const formatDimension = (dim) => {
    if (!dim) return "-";
    const h = dim.height && dim.height.value || "-";
    const w = dim.width && dim.width.value || "-";
    const l = dim.length && dim.length.value || "-";
    const unit = dim.height && dim.height.unit || dim.width && dim.width.unit || dim.length && dim.length.unit || "";
    return `${l}"D x ${w}"W x ${h}"H${unit ? " (" + unit + ")" : ""}`;
  };

  //DATA EXTRACTIONS 
  const itemName = getAttr("item_name");
  const identifiers = (data && data.identifiers && data.identifiers[0] && data.identifiers[0].identifiers) || [];
  const imageList = (data && data.images && data.images[0] && data.images[0].images) || [];
  const productTypes = (data && data.productTypes && data.productTypes.map(function (p) { return p.productType; })) || [];
  const relationships = (data && data.relationships && data.relationships[0] && data.relationships[0].relationships) || [];
  const salesRanks = (data && data.salesRanks && data.salesRanks[0]) || {};
  const summaries = (data && data.summaries && data.summaries[0]) || {};
  const bulletPoints = attributes.bullet_point || [];
  const description = (attributes.product_description && attributes.product_description[0] && attributes.product_description[0].value) || "-";

  // Arrow icon component 
  const ArrowIcon = ({ isExpanded }) => (
    <span className="arrow-icon">
      {isExpanded ? "▼" : "►"}
    </span>
  );

  // CHECK FOR NO DATA AFTER ALL HOOKS AND VARIABLES
  const hasNoData = !data || 
                    Object.keys(data).length === 0 || 
                    (!data.attributes && !data.identifiers && !data.images && !data.productTypes && 
                     !data.relationships && !data.salesRanks && !data.summaries && !data.dimensions);

  // ALL YOUR EXISTING generatePdf FUNCTION - COMPLETELY UNCHANGED
  const generatePdf = (data) => {
    const doc = new jsPDF();
    let y = 25; // Better top margin
    const pageHeight = 290;
    const marginBottom = 25; // Better bottom margin
    const leftMargin = 20; // Better left margin
    const rightMargin = 20;
    const pageWidth = 170; // Adjusted for better margins

    // Helper function to safely get attribute values
    const getAttributeValue = (attrName) => {
      if (attributes && attributes[attrName] && attributes[attrName][0]) {
        return attributes[attrName][0].value || "-";
      }
      return "-";
    };

    // Helper function to check if we need a new page
    const checkPageBreak = (additionalHeight = 15) => {
      if (y + additionalHeight > pageHeight - marginBottom) {
        doc.addPage();
        y = 25; // Reset to better top margin
        return true;
      }
      return false;
    };

    // Helper function to draw section header with better spacing
    const drawSectionHeader = (title) => {
      checkPageBreak(25); // More space needed
      
      // Add space before header
      y += 5;
      
      // Set header background color (simulate your gray headers)
      doc.setFillColor(233, 236, 239); // #e9ecef
      doc.rect(leftMargin, y - 4, pageWidth, 12, 'F');
      
      // Set header text
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(73, 80, 87); // #495057
      doc.text(title, leftMargin + 5, y + 4);
      
      y += 15; // More space after header
    };

    // Helper function to detect URLs
    const isUrl = (text) => {
      try {
        const urlPattern = /^(https?:\/\/[^\s]+)/i;
        return urlPattern.test(text);
      } catch {
        return false;
      }
    };

    // Helper function to draw key-value pairs with better spacing
    const drawKeyValueRow = (key, value) => {
      if (!value || value === "-") return;
      
      checkPageBreak(15);
      
      // Set alternating background for better readability
      if (Math.floor((y - 25) / 15) % 2 === 0) {
        doc.setFillColor(248, 249, 250); // #f8f9fa
        doc.rect(leftMargin, y - 3, pageWidth, 12, 'F');
      }
      
      // Draw key (label)
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(73, 80, 87); // #495057
      doc.text(key, leftMargin + 3, y + 4);
      
      // Draw value with URL detection
      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 37, 41); // #212529
      
      const valueText = `${String(value)}`; // Removed colon
      const valueStartX = leftMargin + 70; // Better positioning
      
      // Check if the entire value is a URL
      if (isUrl(String(value))) {
        // Format as clickable blue link
        doc.setTextColor(0, 0, 255); // Blue color
        const splitText = doc.splitTextToSize(valueText, pageWidth - 75);
        doc.text(splitText, valueStartX, y + 4);
        
        // Add underline for the URL
        splitText.forEach((line, index) => {
          const lineWidth = doc.getTextWidth(line);
          doc.line(valueStartX, y + 4 + (index * 5) + 1, valueStartX + lineWidth, y + 4 + (index * 5) + 1);
        });
        
        // Reset color
        doc.setTextColor(33, 37, 41);
      } else {
        // Handle regular text
        const splitText = doc.splitTextToSize(valueText, pageWidth - 75);
        doc.text(splitText, valueStartX, y + 4);
      }
      
      const splitText = doc.splitTextToSize(valueText, pageWidth - 75);
      y += Math.max(12, splitText.length * 5 + 3);
    };

    // Helper function to draw bullet points with better spacing
    const drawBulletPoint = (text) => {
      checkPageBreak(12);
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 37, 41);
      
      const bulletText = `• ${text}`;
      const splitText = doc.splitTextToSize(bulletText, pageWidth - 15);
      doc.text(splitText, leftMargin + 8, y);
      
      y += splitText.length * 5 + 5; // Better spacing between bullet points
    };

    // Helper function to load images
    const loadImage = (imageUrl) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = function() {
          resolve({
            element: img,
            width: img.width,
            height: img.height,
            loaded: true
          });
        };
        
        img.onerror = function() {
          resolve({
            element: null,
            width: 0,
            height: 0,
            loaded: false
          });
        };
        
        img.src = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
      });
    };

    // Process images with UI-like sizing (moved to bottom)
const processImagesLikeUI = async () => {
  if (!imageList || imageList.length === 0) {
    return;
  }
  
  const imageUrls = imageList.map(img => img.link).filter(Boolean);
  const variants = imageList.map(img => img.variant || '');
  
  if (imageUrls.length === 0) {
    return;
  }

  // Load all images first
  const loadedImages = [];
  for (let i = 0; i < imageUrls.length; i++) {
    if (imageUrls[i]) {
      const imageData = await loadImage(imageUrls[i]);
      if (imageData.loaded) {
        loadedImages.push({
          ...imageData,
          url: imageUrls[i],
          variant: variants[i] || `Image ${i + 1}`,
          width: (imageList[i] && imageList[i].width) || 'N/A',
          height: (imageList[i] && imageList[i].height) || 'N/A',
          index: i
        });
      }
    }
  }
  
  // Only show section if we have images
  if (loadedImages.length > 0) {
    drawSectionHeader("Product Images");
    
    // UI-like image layout - 
    const imagesPerRow = 5;
    const imageSize = 25;
    const imageSpacing = 4;
    const rowHeight = imageSize + 15;
    
    // Calculate total width and center
    const totalImageWidth = (imagesPerRow * imageSize) + ((imagesPerRow - 1) * imageSpacing);
    const startX = leftMargin + (pageWidth - totalImageWidth) / 2;
    
    const totalRows = Math.ceil(loadedImages.length / imagesPerRow);
    
    for (let row = 0; row < totalRows; row++) {
      const startIndex = row * imagesPerRow;
      const endIndex = Math.min(startIndex + imagesPerRow, loadedImages.length);
      const imagesInRow = loadedImages.slice(startIndex, endIndex);
      
      if (y + rowHeight > pageHeight - marginBottom) {
        doc.addPage();
        y = 25;
      }
      
      const startY = y;
      
      imagesInRow.forEach((imageData, indexInRow) => {
        const xPosition = startX + (indexInRow * (imageSize + imageSpacing));
        
        try {
          // Add border like UI
          doc.setDrawColor(222, 226, 230);
          doc.setLineWidth(0.5);
          doc.rect(xPosition - 1, startY - 1, imageSize + 2, imageSize + 2);
          
          // Add padding like UI
          const paddedSize = imageSize - 4;
          const paddedPosition = xPosition + 2;
          const paddedY = startY + 2;
          
          // Add clickable image
          doc.addImage(
            imageData.element, 
            'JPEG', 
            paddedPosition, 
            paddedY, 
            paddedSize, 
            paddedSize,
            undefined,
            'FAST'
          );
          
          // Make image clickable
          doc.link(
            paddedPosition, 
            paddedY, 
            paddedSize, 
            paddedSize, 
            { url: imageData.url }
          );
          
          // Add text below image
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(108, 117, 125);
          
          const variantText = imageData.variant || `Image ${imageData.index + 1}`;
          const wrappedText = doc.splitTextToSize(variantText, imageSize);
          
          let textY = startY + imageSize + 7;
          
          // 1. Variant text
          wrappedText.forEach((line, lineIndex) => {
            const textWidth = doc.getTextWidth(line);
            const textX = xPosition + (imageSize - textWidth) / 2;
            doc.text(line, textX, textY);
            textY += 3;
          });
          
          // 2. Width × Height
          if (imageData.width !== 'N/A' && imageData.height !== 'N/A') {
            const dimensionsText = `${imageData.width} × ${imageData.height}`;
            const dimensionsWidth = doc.getTextWidth(dimensionsText);
            const dimensionsX = xPosition + (imageSize - dimensionsWidth) / 2;
            doc.text(dimensionsText, dimensionsX, textY + 2);
          }
          
        } catch (error) {
          console.error(`Failed to add image ${imageData.index + 1}:`, error);
        }
      });
      
      y += rowHeight +3;
    }
    
    y += 8;
  }
};



    // Helper function for links (sales ranks)
    const drawLinkRow = (key, linkUrl) => {
      checkPageBreak(15);
      
      // Same styling as drawKeyValueRow
      if (Math.floor((y - 25) / 15) % 2 === 0) {
        doc.setFillColor(248, 249, 250);
        doc.rect(leftMargin, y - 3, pageWidth, 12, 'F');
      }
      
      // Draw key
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(73, 80, 87);
      doc.text(key, leftMargin + 3, y + 4);
      
      // Draw link in blue
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 255); // Blue
      const linkText = `${linkUrl}`; // Removed colon
      const splitLink = doc.splitTextToSize(linkText, pageWidth - 75);
      doc.text(splitLink, leftMargin + 70, y + 4);
      
      // Add underline
      splitLink.forEach((line, index) => {
        const lineWidth = doc.getTextWidth(line);
        doc.line(leftMargin + 70, y + 4 + (index * 5) + 1, leftMargin + 70 + lineWidth, y + 4 + (index * 5) + 1);
      });
      
      // Reset color
      doc.setTextColor(33, 37, 41);
      
      y += Math.max(12, splitLink.length * 5 + 3);
    };

    // Helper function to format camelCase labels to proper spaced words
    const formatLabelName = (key) => {
      const specialCases = {
        'asin': 'ASIN', 'sku': 'SKU', 'upc': 'UPC', 'ean': 'EAN', 'isbn': 'ISBN', 'api': 'API', 'url': 'URL', 'id': 'ID'
      };
      
      if (specialCases[key.toLowerCase()]) {
        return specialCases[key.toLowerCase()];
      }
      
      let formatted = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      
      Object.keys(specialCases).forEach(special => {
        const regex = new RegExp(`\\b${special}\\b`, 'gi');
        formatted = formatted.replace(regex, specialCases[special]);
      });
      
      return formatted;
    };

    // MAIN PDF GENERATION FUNCTION - REORDERED SECTIONS
    const generatePdfContent = async () => {
      try {
        // 1. PRODUCT TITLE
        if (itemName && itemName !== "-") {
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(73, 80, 87);
          
          const titleLines = doc.splitTextToSize(itemName, pageWidth);
          titleLines.forEach(line => {
            doc.text(line, leftMargin + pageWidth/2, y, { align: 'center' });
            y += 8;
          });
          y += 15; // Better spacing after title
        }

        // 2. KEY FEATURES
        if (bulletPoints && bulletPoints.length > 0) {
          drawSectionHeader("Key Features");
          bulletPoints.forEach(bp => {
            if (bp.value) {
              drawBulletPoint(bp.value);
            }
          });
        }

        // 3. PRODUCT DESCRIPTION
        if (description && description !== "-") {
          drawSectionHeader("Product Description");
          const cleanDescription = description.replace(/<\/?[^>]+(>|$)/g, "");
          const descLines = doc.splitTextToSize(cleanDescription, pageWidth - 15);
          descLines.forEach(line => {
            checkPageBreak(8);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(33, 37, 41);
            doc.text(line, leftMargin + 8, y);
            y += 6;
          });
          y += 8;
        }

        // 4. SALES RANKS
        if (salesRanks && Object.keys(salesRanks).length > 0) {
          drawSectionHeader("Sales Ranks");
          
          if (salesRanks.marketplaceId) {
            drawKeyValueRow("Marketplace ID", salesRanks.marketplaceId);
          }

          if (salesRanks.classificationRanks && salesRanks.classificationRanks.length > 0) {
            drawKeyValueRow("Classification Ranks", "");
            salesRanks.classificationRanks.forEach((rank, idx) => {
              if (rank.title) drawKeyValueRow("    Title", renderValue(rank.title));
              if (rank.classificationId) drawKeyValueRow("    Classification ID", renderValue(rank.classificationId));
              if (rank.rank) drawKeyValueRow("    Rank", `#${renderValue(rank.rank)}`);
              if (rank.link) drawLinkRow("    Link", rank.link);
            });
          }

          if (salesRanks.displayGroupRanks && salesRanks.displayGroupRanks.length > 0) {
            drawKeyValueRow("Display Group Ranks", "");
            salesRanks.displayGroupRanks.forEach((rank, idx) => {
              if (rank.title) drawKeyValueRow("    Title", renderValue(rank.title));
              if (rank.websiteDisplayGroup) drawKeyValueRow("    Website Display Group", renderValue(rank.websiteDisplayGroup));
              if (rank.rank) drawKeyValueRow("    Rank", `#${renderValue(rank.rank)}`);
              if (rank.link) drawLinkRow("    Link", rank.link);
            });
          }
        }

        // 5. DIMENSIONS SECTION
        if (data.dimensions && data.dimensions.length > 0) {
          drawSectionHeader("Dimensions");
          data.dimensions.forEach((dim, idx) => {
            if (dim.marketplaceId) {
              drawKeyValueRow("Marketplace ID", dim.marketplaceId);
            }
            if (dim.item) {
              drawKeyValueRow("Item Dimensions", formatDimension(dim.item));
            }
            if (dim.package) {
              drawKeyValueRow("Package Dimensions", formatDimension(dim.package));
            }
          });
        }

        // 6. ATTRIBUTES SECTION
        if (attributes && Object.keys(attributes).length > 0) {
          drawSectionHeader("Attributes");
          
          const attributeFields = [
            { key: 'item_name', label: 'Item Name' },
            { key: 'brand', label: 'Brand' },
            { key: 'manufacturer', label: 'Manufacturer' },
            { key: 'material', label: 'Material' },
            { key: 'color', label: 'Color' },
            { key: 'theme', label: 'Theme' },
            { key: 'bottle_type', label: 'Bottle Type' },
            { key: 'reusability', label: 'Reusability' },
            { key: 'finish_type', label: 'Finish Type' },
            { key: 'care_instructions', label: 'Care Instructions' },
            { key: 'item_type_keyword', label: 'Item Type Keyword' },
            { key: 'material_type_free', label: 'Material Type Free' },
            { key: 'recommended_uses_for_product', label: 'Recommended Uses' },
            { key: 'special_feature', label: 'Special Feature' },
            { key: 'item_shape', label: 'Item Shape' },
            { key: 'gpsr_safety_attestation', label: 'GPSR Safety Attestation' },
            { key: 'batteries_required', label: 'Batteries Required' },
            { key: 'batteries_included', label: 'Batteries Included' },
            { key: 'unspsc_code', label: 'UNSPSC Code' },
            { key: 'number_of_items', label: 'Number of Items' }
          ];

          attributeFields.forEach(field => {
            const value = getAttributeValue(field.key);
            if (value && value !== "-") {
              drawKeyValueRow(field.label, value);
            }
          });

          // Handle special cases
          if (attributes.item_dimensions && attributes.item_dimensions[0]) {
            drawKeyValueRow("Item Dimensions", formatDimension(attributes.item_dimensions[0]));
          }
          if (attributes.item_package_dimensions && attributes.item_package_dimensions[0]) {
            drawKeyValueRow("Item Package Dimensions", formatDimension(attributes.item_package_dimensions[0]));
          }
          if (attributes.item_weight && attributes.item_weight[0]) {
            drawKeyValueRow("Item Weight", `${attributes.item_weight[0].value} ${attributes.item_weight[0].unit}`);
          }
          if (attributes.item_package_weight && attributes.item_package_weight[0]) {
            drawKeyValueRow("Package Weight", `${attributes.item_package_weight[0].value} ${attributes.item_package_weight[0].unit}`);
          }
          if (attributes.capacity && attributes.capacity[0]) {
            drawKeyValueRow("Capacity", `${attributes.capacity[0].value} ${attributes.capacity[0].unit}`);
          }
          if (attributes.unit_count && attributes.unit_count[0]) {
            const unitType = attributes.unit_count[0].type && attributes.unit_count[0].type.value ? attributes.unit_count[0].type.value : '';
            drawKeyValueRow("Unit Count", `${attributes.unit_count[0].value} ${unitType}`);
          }
          if (attributes.product_site_launch_date && attributes.product_site_launch_date[0]) {
            drawKeyValueRow("Launch Date", new Date(attributes.product_site_launch_date[0].value).toLocaleDateString());
          }
          if (attributes.variation_theme && attributes.variation_theme[0]) {
            drawKeyValueRow("Variation Theme", attributes.variation_theme[0].name);
          }
          if (attributes.list_price && attributes.list_price[0]) {
            drawKeyValueRow("List Price", `${attributes.list_price[0].value} ${attributes.list_price[0].currency}`);
          }
          if (attributes.container && attributes.container[0] && attributes.container[0].type && attributes.container[0].type[0]) {
            drawKeyValueRow("Container", attributes.container[0].type[0].value);
          }
        }

        // 7. EXTERNAL IDENTIFIERS
        if (attributes.externally_assigned_product_identifier && attributes.externally_assigned_product_identifier.length > 0) {
          drawSectionHeader("External Identifiers");
          attributes.externally_assigned_product_identifier.forEach(id => {
            if (id.type && id.value) {
              drawKeyValueRow(id.type.toUpperCase(), `${id.value} (${id.marketplace_id || ''})`);
            }
          });
        }

        // 8. IDENTIFIERS
        if (identifiers && identifiers.length > 0) {
          drawSectionHeader("Identifiers");
          identifiers.forEach(id => {
            drawKeyValueRow(renderValue(id.identifierType), renderValue(id.identifier));
          });
        }

        // 9. PRODUCT TYPES
        if (productTypes && productTypes.length > 0) {
          drawSectionHeader("Product Types");
          productTypes.forEach(pt => {
            drawBulletPoint(renderValue(pt));
          });
        }

        // 10. RELATIONSHIPS
        if (relationships && relationships.length > 0) {
          drawSectionHeader("Relationships");
          if (data.relationships && data.relationships[0] && data.relationships[0].marketplaceId) {
            drawKeyValueRow("Marketplace ID", data.relationships[0].marketplaceId);
          }
          relationships.forEach(rel => {
            if (rel.type) {
              drawKeyValueRow("Type", rel.type);
            }
            if (rel.parentAsins && rel.parentAsins.length > 0) {
              drawKeyValueRow("Parent ASINs", rel.parentAsins.join(", "));
            }
            if (rel.variationTheme) {
              drawKeyValueRow("Variation Theme", rel.variationTheme.theme || "-");
              if (rel.variationTheme.attributes && rel.variationTheme.attributes.length > 0) {
                drawKeyValueRow("Variation Attributes", rel.variationTheme.attributes.join(", "));
              }
            }
          });
        }

        // 11. SUMMARY
        if (summaries && Object.keys(summaries).length > 0) {
          drawSectionHeader("Summary");
          Object.keys(summaries).forEach(key => {
            const formattedLabel = formatLabelName(key);
            drawKeyValueRow(formattedLabel, renderValue(summaries[key]));
          });
        }

        // 12. PRODUCT IMAGES - AT THE BOTTOM
        await processImagesLikeUI();

        // Save PDF
        const safeName = (itemName || "product").replace(/[\/\\?%*:|"<>]/g, "_");
        doc.save(`${safeName}.pdf`);

      } catch (error) {
        console.error('Error generating PDF:', error);
        const safeName = (itemName || "product").replace(/[\/\\?%*:|"<>]/g, "_");
        doc.save(`${safeName}.pdf`);
      }
    };

    // Start PDF generation
    generatePdfContent();
  };

  return (
    <>
      <Fragment>
        <Container>
          <Row>
            {hasNoData ? (
              <Col md={12} className="d-flex justify-content-center">
                <div>
                  <p>
                    Product Catalog Details are not found.
                  </p>
                </div>
              </Col>
            ) : (
              <>
                <Col md={12} className='mb-2 d-flex justify-content-end'>
                
                 <button
                    title="Download PDF"
                    className="info-button-background-none"
                    onClick={() => generatePdf(data)}
                  >
                    <img style={{height:'22px'}} src={DownloadIcon}/>
                    {/* <FontAwesomeIcon style={{ fontSize: '27px' }} icon={faDownload} /> */}
                  </button>
                  
                </Col>
                <Col md={12} className="d-flex justify-content-center">
                  
                  <div className="catalogue-container">

                    {/* PRODUCT TITLE*/}
                    {itemName && itemName !== "-" && (
                      <h3 className="product-title">
                        {itemName}
                      </h3>
                    )}

                    {/* ===================== 1. KEY FEATURES ===================== */}
                    {bulletPoints.length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('keyFeatures')}
                              colSpan="2"
                            >
                              Key Features
                              <ArrowIcon isExpanded={expandedSections.keyFeatures} />
                            </td>
                          </tr>

                          {expandedSections.keyFeatures && (
                            <tr className="table-row">
                              <td className="value-cell" colSpan="2">
                                {bulletPoints.map((bp, idx) => (
                                  <div key={idx} className="bullet-item">
                                    • {bp.value}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 2. DESCRIPTION ===================== */}
                    {description && description !== "-" && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('description')}
                              colSpan="2"
                            >
                              Product Description
                              <ArrowIcon isExpanded={expandedSections.description} />
                            </td>
                          </tr>

                          {expandedSections.description && (
                            <tr className="table-row">
                              <td className="value-cell" colSpan="2">
                                <div dangerouslySetInnerHTML={{ __html: description }} />
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 3. SALES RANKS ===================== */}
                    {salesRanks && Object.keys(salesRanks).length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('salesRanks')}
                              colSpan="2"
                            >
                              Sales Ranks
                              <ArrowIcon isExpanded={expandedSections.salesRanks} />
                            </td>
                          </tr>

                          {expandedSections.salesRanks && (
                            <>
                              {salesRanks.marketplaceId && (
                                <tr className="table-row">
                                  <td className="label-cell">Marketplace ID</td>
                                  <td className="value-cell">{salesRanks.marketplaceId}</td>
                                </tr>
                              )}

                              {/* Classification Ranks */}
                              {salesRanks.classificationRanks && salesRanks.classificationRanks.length > 0 && (
                                <>
                                  <tr className="table-row">
                                    <td className="label-cell">Classification Ranks</td>
                                    <td className="value-cell"></td>
                                  </tr>
                                  {salesRanks.classificationRanks.map((rank, idx) => (
                                    <React.Fragment key={idx}>
                                      <tr className="table-row">
                                        <td className="nested-label">Title</td>
                                        <td className="value-cell">{renderValue(rank.title)}</td>
                                      </tr>
                                      <tr className="table-row">
                                        <td className="nested-label">Classification ID</td>
                                        <td className="value-cell">{renderValue(rank.classificationId)}</td>
                                      </tr>
                                      <tr className="table-row">
                                        <td className="nested-label">Rank</td>
                                        <td className="value-cell">#{renderValue(rank.rank)}</td>
                                      </tr>
                                      {rank.link && (
                                        <tr className="table-row">
                                          <td className="nested-label">Link</td>
                                          <td className="value-cell">
                                            <a href={rank.link} target="_blank" rel="noreferrer" className="sales-rank-link">
                                              {rank.link}
                                            </a>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </>
                              )}

                              {/* Display Group Ranks */}
                              {salesRanks.displayGroupRanks && salesRanks.displayGroupRanks.length > 0 && (
                                <>
                                  <tr className="table-row">
                                    <td className="label-cell">Display Group Ranks</td>
                                    <td className="value-cell"></td>
                                  </tr>
                                  {salesRanks.displayGroupRanks.map((rank, idx) => (
                                    <React.Fragment key={idx}>
                                      <tr className="table-row">
                                        <td className="nested-label">Title</td>
                                        <td className="value-cell">{renderValue(rank.title)}</td>
                                      </tr>
                                      <tr className="table-row">
                                        <td className="nested-label">Website Display Group</td>
                                        <td className="value-cell">{renderValue(rank.websiteDisplayGroup)}</td>
                                      </tr>
                                      <tr className="table-row">
                                        <td className="nested-label">Rank</td>
                                        <td className="value-cell">#{renderValue(rank.rank)}</td>
                                      </tr>
                                      {rank.link && (
                                        <tr className="table-row">
                                          <td className="nested-label">Link</td>
                                          <td className="value-cell">
                                            <a href={rank.link} target="_blank" rel="noreferrer" className="sales-rank-link">
                                              {rank.link}
                                            </a>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 4. DIMENSIONS SECTION ===================== */}
                    {data.dimensions && data.dimensions.length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('dimensions')}
                              colSpan="2"
                            >
                              Dimensions
                              <ArrowIcon isExpanded={expandedSections.dimensions} />
                            </td>
                          </tr>

                          {expandedSections.dimensions && (
                            <>
                              {data.dimensions.map((dim, idx) => (
                                <React.Fragment key={idx}>
                                  <tr className="table-row">
                                    <td className="label-cell">Marketplace ID</td>
                                    <td className="value-cell">{dim.marketplaceId || "-"}</td>
                                  </tr>
                                  {dim.item && (
                                    <tr className="table-row">
                                      <td className="label-cell">Item Dimensions</td>
                                      <td className="value-cell">{formatDimension(dim.item)}</td>
                                    </tr>
                                  )}
                                  {dim.package && (
                                    <tr className="table-row">
                                      <td className="label-cell">Package Dimensions</td>
                                      <td className="value-cell">{formatDimension(dim.package)}</td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 5. ATTRIBUTES SECTION ===================== */}
                    {attributes && Object.keys(attributes).length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('attributes')}
                              colSpan="2"
                            >
                              Attributes
                              <ArrowIcon isExpanded={expandedSections.attributes} />
                            </td>
                          </tr>

                          {expandedSections.attributes && (
                            <>
                              {/*  ATTRIBUTE */}
                              {attributes.item_name && attributes.item_name[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Item Name</td>
                                  <td className="value-cell">{attributes.item_name[0].value}</td>
                                </tr>
                              )}

                              {attributes.brand && attributes.brand[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Brand</td>
                                  <td className="value-cell">{attributes.brand[0].value}</td>
                                </tr>
                              )}

                              {attributes.manufacturer && attributes.manufacturer[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Manufacturer</td>
                                  <td className="value-cell">{attributes.manufacturer[0].value}</td>
                                </tr>
                              )}

                              {attributes.material && attributes.material[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Material</td>
                                  <td className="value-cell">{attributes.material[0].value}</td>
                                </tr>
                              )}

                              {attributes.color && attributes.color[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Color</td>
                                  <td className="value-cell">{attributes.color[0].value}</td>
                                </tr>
                              )}

                              {attributes.theme && attributes.theme[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Theme</td>
                                  <td className="value-cell">{attributes.theme[0].value}</td>
                                </tr>
                              )}

                              {attributes.bottle_type && attributes.bottle_type[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Bottle Type</td>
                                  <td className="value-cell">{attributes.bottle_type[0].value}</td>
                                </tr>
                              )}

                              {attributes.reusability && attributes.reusability[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Reusability</td>
                                  <td className="value-cell">{attributes.reusability[0].value}</td>
                                </tr>
                              )}

                              {attributes.finish_type && attributes.finish_type[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Finish Type</td>
                                  <td className="value-cell">{attributes.finish_type[0].value}</td>
                                </tr>
                              )}

                              {attributes.container && attributes.container[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Container</td>
                                  <td className="value-cell">{attributes.container[0].type && attributes.container[0].type[0] && attributes.container[0].type[0].value}</td>
                                </tr>
                              )}

                              {attributes.care_instructions && attributes.care_instructions[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Care Instructions</td>
                                  <td className="value-cell">{attributes.care_instructions[0].value}</td>
                                </tr>
                              )}

                              {attributes.item_type_keyword && attributes.item_type_keyword[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Item Type Keyword</td>
                                  <td className="value-cell">{attributes.item_type_keyword[0].value}</td>
                                </tr>
                              )}

                              {attributes.material_type_free && attributes.material_type_free[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Material Type Free</td>
                                  <td className="value-cell">{attributes.material_type_free[0].value}</td>
                                </tr>
                              )}

                              {attributes.recommended_uses_for_product && attributes.recommended_uses_for_product[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Recommended Uses</td>
                                  <td className="value-cell">{attributes.recommended_uses_for_product[0].value}</td>
                                </tr>
                              )}

                              {attributes.special_feature && attributes.special_feature[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Special Feature</td>
                                  <td className="value-cell">{attributes.special_feature[0].value}</td>
                                </tr>
                              )}

                              {attributes.item_shape && attributes.item_shape[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Item Shape</td>
                                  <td className="value-cell">{attributes.item_shape[0].value}</td>
                                </tr>
                              )}

                              {attributes.gpsr_safety_attestation && attributes.gpsr_safety_attestation[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">GPSR Safety Attestation</td>
                                  <td className="value-cell">{String(attributes.gpsr_safety_attestation[0].value)}</td>
                                </tr>
                              )}

                              {attributes.batteries_required && attributes.batteries_required[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Batteries Required</td>
                                  <td className="value-cell">{String(attributes.batteries_required[0].value)}</td>
                                </tr>
                              )}

                              {attributes.batteries_included && attributes.batteries_included[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Batteries Included</td>
                                  <td className="value-cell">{String(attributes.batteries_included[0].value)}</td>
                                </tr>
                              )}

                              {attributes.unspsc_code && attributes.unspsc_code[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">UNSPSC Code</td>
                                  <td className="value-cell">{attributes.unspsc_code[0].value}</td>
                                </tr>
                              )}

                              {attributes.item_dimensions && attributes.item_dimensions[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Item Dimensions</td>
                                  <td className="value-cell">{formatDimension(attributes.item_dimensions[0])}</td>
                                </tr>
                              )}

                              {attributes.item_package_dimensions && attributes.item_package_dimensions[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Item Package Dimensions</td>
                                  <td className="value-cell">{formatDimension(attributes.item_package_dimensions[0])}</td>
                                </tr>
                              )}

                              {attributes.item_weight && attributes.item_weight[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Item Weight</td>
                                  <td className="value-cell">{attributes.item_weight[0].value} {attributes.item_weight[0].unit}</td>
                                </tr>
                              )}

                              {attributes.item_package_weight && attributes.item_package_weight[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Package Weight</td>
                                  <td className="value-cell">{attributes.item_package_weight[0].value} {attributes.item_package_weight[0].unit}</td>
                                </tr>
                              )}

                              {attributes.capacity && attributes.capacity[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Capacity</td>
                                  <td className="value-cell">{attributes.capacity[0].value} {attributes.capacity[0].unit}</td>
                                </tr>
                              )}

                              {attributes.unit_count && attributes.unit_count[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Unit Count</td>
                                  <td className="value-cell">{attributes.unit_count[0].value} {attributes.unit_count[0].type && attributes.unit_count[0].type.value}</td>
                                </tr>
                              )}

                              {attributes.number_of_items && attributes.number_of_items[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Number of Items</td>
                                  <td className="value-cell">{attributes.number_of_items[0].value}</td>
                                </tr>
                              )}

                              {attributes.product_site_launch_date && attributes.product_site_launch_date[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Launch Date</td>
                                  <td className="value-cell">{new Date(attributes.product_site_launch_date[0].value).toLocaleDateString()}</td>
                                </tr>
                              )}

                              {attributes.variation_theme && attributes.variation_theme[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">Variation Theme</td>
                                  <td className="value-cell">{attributes.variation_theme[0].name}</td>
                                </tr>
                              )}

                              {attributes.list_price && attributes.list_price[0] && (
                                <tr className="table-row">
                                  <td className="label-cell">List Price</td>
                                  <td className="value-cell">{attributes.list_price[0].value} {attributes.list_price[0].currency}</td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 6. EXTERNAL IDENTIFIERS ===================== */}
                    {attributes.externally_assigned_product_identifier && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('externalIdentifiers')}
                              colSpan="2"
                            >
                              External Identifiers
                              <ArrowIcon isExpanded={expandedSections.externalIdentifiers} />
                            </td>
                          </tr>

                          {expandedSections.externalIdentifiers && (
                            <>
                              {attributes.externally_assigned_product_identifier.map((id, idx) => (
                                <tr key={idx} className="table-row">
                                  <td className="label-cell">{id.type && id.type.toUpperCase()}</td>
                                  <td className="value-cell">{id.value} ({id.marketplace_id})</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 7. IDENTIFIERS ===================== */}
                    {identifiers.length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('identifiers')}
                              colSpan="2"
                            >
                              Identifiers
                              <ArrowIcon isExpanded={expandedSections.identifiers} />
                            </td>
                          </tr>

                          {expandedSections.identifiers && (
                            <>
                              {identifiers.map((id, idx) => (
                                <tr key={idx} className="table-row">
                                  <td className="label-cell">{renderValue(id.identifierType)}</td>
                                  <td className="value-cell">{renderValue(id.identifier)}</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 8. PRODUCT TYPES ===================== */}
                    {productTypes.length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('productTypes')}
                              colSpan="2"
                            >
                              Product Types
                              <ArrowIcon isExpanded={expandedSections.productTypes} />
                            </td>
                          </tr>

                          {expandedSections.productTypes && (
                            <tr className="table-row">
                              <td className="value-cell" colSpan="2">
                                {productTypes.map((pt, idx) => (
                                  <div key={idx} className="bullet-item">
                                    • {renderValue(pt)}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 9. RELATIONSHIPS ===================== */}
                    {relationships && relationships.length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('relationships')}
                              colSpan="2"
                            >
                              Relationships
                              <ArrowIcon isExpanded={expandedSections.relationships} />
                            </td>
                          </tr>

                          {expandedSections.relationships && (
                            <>
                              {data.relationships && data.relationships.length > 0 && data.relationships[0].marketplaceId && (
                                <tr className="table-row">
                                  <td className="label-cell">Marketplace ID</td>
                                  <td className="value-cell">{data.relationships[0].marketplaceId}</td>
                                </tr>
                              )}

                              {relationships.map((rel, idx) => (
                                <React.Fragment key={idx}>
                                  <tr className="table-row">
                                    <td className="label-cell">Type</td>
                                    <td className="value-cell">{rel.type || "-"}</td>
                                  </tr>

                                  {rel.parentAsins && rel.parentAsins.length > 0 && (
                                    <tr className="table-row">
                                      <td className="label-cell">Parent ASINs</td>
                                      <td className="value-cell">{rel.parentAsins.join(", ")}</td>
                                    </tr>
                                  )}

                                  {rel.variationTheme && (
                                    <>
                                      <tr className="table-row">
                                        <td className="label-cell">Variation Theme</td>
                                        <td className="value-cell">{rel.variationTheme.theme || "-"}</td>
                                      </tr>
                                      {rel.variationTheme.attributes && rel.variationTheme.attributes.length > 0 && (
                                        <tr className="table-row">
                                          <td className="label-cell">Variation Attributes</td>
                                          <td className="value-cell">{rel.variationTheme.attributes.join(", ")}</td>
                                        </tr>
                                      )}
                                    </>
                                  )}
                                </React.Fragment>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 10. SUMMARIES ===================== */}
                    {summaries && Object.keys(summaries).length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('summaries')}
                              colSpan="2"
                            >
                              Summary
                              <ArrowIcon isExpanded={expandedSections.summaries} />
                            </td>
                          </tr>

                          {expandedSections.summaries && (
                            <>
                              {Object.keys(summaries).map((key, idx) => {
                                // Format camelCase to proper words
                                const formatLabel = (str) => {
                                  return str
                                    .replace(/([A-Z])/g, ' $1')
                                    .replace(/^./, char => char.toUpperCase())
                                    .trim();
                                };

                                return (
                                  <tr key={idx} className="table-row">
                                    <td className="label-cell">{formatLabel(key)}</td>
                                    <td className="value-cell">{renderValue(summaries[key])}</td>
                                  </tr>
                                );
                              })}
                            </>
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* ===================== 11. PRODUCT IMAGES - AT BOTTOM ===================== */}
                    {imageList.length > 0 && (
                      <table className="data-table">
                        <tbody>
                          <tr>
                            <td
                              className="expandable-header"
                              onClick={() => toggleSection('images')}
                              colSpan="2"
                            >
                              Product Images
                              <ArrowIcon isExpanded={expandedSections.images} />
                            </td>
                          </tr>

                          {expandedSections.images && (
                            <tr className="table-row">
                              <td className="value-cell" colSpan="2">
                              <div className="image-gallery">
                              {imageList.map((img, idx) => (
                                <div key={idx} className="image-container">
                                   <a href={img.link} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={img.link}
                                      alt={itemName}
                                      className="product-image"
                                    />
                                  </a>
                                  <div className="image-variant">
                                    <p className="mb-0">{renderValue(img.variant)}</p>
                                    <p className="mb-0">{img.width} &#10006; {img.height}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}

                  </div>
                </Col>
              </>
            )}
          </Row>
        </Container>
      </Fragment>
    </>
  );
};

export default CatalogueItemDetails;
